import { atom } from 'recoil';
import Constant from '@/common/constant/Constant';

/**
 * Re-render signal for auto-translation - deliberately just an integer.
 *
 * The dictionary itself lives in translationStore (a module-level Map) and is
 * persisted by translationDb (IndexedDB). It used to live in this atom, which
 * meant every merge of a few new translations spread-copied the entire
 * dictionary; that does not survive tag/field/content translation, where an org
 * can reach hundreds of thousands of entries.
 *
 * translationStore bumps its version on every mutation and the loader mirrors
 * that number here, so a write is O(1) and components still repaint.
 * Read it via useTranslationSubscription().
 */
export const translationVersionAtom = atom({
    key: 'translationVersionAtom',
    default: 0
});

/**
 * Signals that the effective display language changed (org toggled 多言語対応, or
 * the user changed their main language) while the page is already open. When
 * true, WrapLayout shows the shared COMMSG030 dialog; the user confirms and the
 * page reloads so every open screen renders consistently in the new language.
 *
 * Set by useTranslationDictionaryLoader on a real transition only - never on the
 * first mount, login, or organization switch.
 */
export const translationReloadDialogAtom = atom({
    key: 'translationReloadDialogAtom',
    default: false
})

/**
 * On-demand content translation state for ticket detail, keyed by ticketId.
 *
 * Used by the on-demand content translation feature for all ticket types. It
 * lives in memory only: translations are kept while navigating panels/tabs but
 * are discarded on browser refresh or logout.
 *
 * Shape:
 * {
 *   [ticketId]: {
 *     ticketType,
 *     status: 'idle' | 'loading' | 'translated' | 'error',  // translation progress
 *     activeView: 'original' | 'translation',               // 「翻訳版」toggle as the USER set it (CR-303134 BR-0): a running or
 *                                                           // failed translation never turns it off; fields without a
 *                                                           // translation just keep showing the original
 *     ticketMode: 'VIEW' | 'EDIT',                          // detail screen mode; EDIT forces original on tab/header/fields
 *     ticketOutline: string,                                // title as rendered by the detail header (FE-computed)
 *     translatedOutline: string|null,                       // translated title, null until ready
 *     fields: {
 *       [fieldKey]: {
 *         sysField,
 *         type,
 *         original: any,           // source value, initialized from ticket content
 *         translated: string|null  // translated value, null until the translation is ready
 *       }
 *     }
 *   }
 * }
 */
export const contentDetailOnDemandAtom = atom({
    key: 'contentDetailOnDemandAtom',
    default: {}
});

/**
 * On-demand chat-thread translation state (CHT-005), keyed by chatThreadId.
 *
 * Unlike contentDetailOnDemandAtom (field-based, per content), a chat thread is a
 * list of messages (ChatPost): the toggle status and its active view live at the
 * thread level (the 「翻訳版」toggle translates the whole thread at once), while each
 * message also keeps its own view so a single message can be flipped independently
 * (§4.2). In-memory only: translations survive panel/tab navigation but are dropped
 * on refresh / logout / switch project.
 *
 * Shape:
 * {
 *   [threadId]: {
 *     toggleStatus: 'idle' | 'loading' | 'translated' | 'error',   // whole-thread 「翻訳版」toggle
 *     activeView: 'original' | 'translation',                      // thread-level view: ON = translation, OFF = original
 *     threadType,                                                  // Constant.THREAD_TYPE
 *     ticketId: string,                                            // ticket the thread belongs to (lets a chat TAB, keyed by ticketId, find the title)
 *     ticketOutline: string,                                       // ticket/knowledge title as rendered by the chat header (FE-computed)
 *     translatedTicketOutline: string|null,                        // translated title, null until ready
 *     messages: {
 *       [postId]: {
 *         postType,                 // Constant.POST_TYPE
 *         creatorId,                // message author (viewer's own text is not translated)
 *         isTranslatable: boolean,  // eligible for translation (spec §5.2/§5.3); drives the toggle link
 *         original: string,         // source content
 *         translated: string|null,  // translated content, null until ready
 *         activeView: 'original' | 'translation',
 *         status: 'idle' | 'loading' | 'translated' | 'error'
 *       }
 *     }
 *   }
 * }
 */
export const chatPostOnDemandAtom = atom({
    key: 'chatPostOnDemandAtom',
    default: {}
});

/**
 * On-demand translations of HTML fields (MLT-001-F3, data-tr-ondmd), so turning the「翻訳版」
 * toggle on again in the same session does not call the API again. In-memory only: dropped on
 * refresh / logout / switch project (BR-11, BR-16). The toggle itself is component state, so
 * reopening the app always starts on the original.
 *
 * Shape: { [`${ticketId}|${fieldId}|${targetLanguage}`]: { [sourceText]: translatedText } }
 */
export const htmlOnDemandTranslationAtom = atom({
    key: 'htmlOnDemandTranslationAtom',
    default: {}
});

/**
 * On-demand translation state for the ticket LIST toolbars (MLT-001-F4).
 * Split into THREE atoms (Q&A-customer / helpdesk / knowledge) so a change on one
 * list does not re-render the others. Each atom is keyed by the current view to
 * isolate views that share one component instance.
 *
 * viewKey: Q&A uses the view enum (Constant.CUSTOMER_SIDE_VIEW.*); helpdesk/knowledge
 * use the view id (viewSelected.id). Both are just unique string keys.
 *
 * The 「翻訳版」toggle lives at the view level; each record keeps its own status so
 * errors stay isolated (BR-12). The list shows one version at a time (§6.1).
 * In-memory only: reset to {} on F5 / logout→login / switch project.
 *
 * The FE stores only translation RESULTS. On toggle ON it sends record ids; the BE
 * fetches each record's title + last message and returns translations. Knowledge has
 * no last message (§5.1), so translatedLastMessage stays null there.
 * Cache key = record id (BR-10): same id → treated as cached, not re-translated.
 *
 * Shape (per atom):
 * {
 *   [viewKey]: {
 *     toggleStatus: 'idle' | 'loading' | 'translated' | 'error',   // whole-list toggle (BR-3b)
 *     activeView: 'original' | 'translation',                      // ON = translation
 *     tickets: {
 *       [ticketId]: {
 *         isTranslatable: boolean,
 *         status: 'idle' | 'loading' | 'translated' | 'error',     // per-record (BR-12)
 *         translatedOutline: string|null,                          // translated title
 *         translatedLastMessage: string|null                       // null for Knowledge
 *       }
 *     }
 *   }
 * }
 */
export const listQAOnDemandAtom = atom({
    key: 'listQAOnDemandAtom',
    default: {}
});

export const listHelpdeskOnDemandAtom = atom({
    key: 'listHelpdeskOnDemandAtom',
    default: {}
});

export const listKnowledgeOnDemandAtom = atom({
    key: 'listKnowledgeOnDemandAtom',
    default: {}
});

/**
 * Resolve the right list-on-demand atom from a list PANEL_TYPE. Lets the shared handler /
 * hook / control pick the correct per-screen atom from the `panelType` they already have,
 * so callers only pass panelType + viewKey (no atom object threaded through props).
 *
 * @param {string} panelType - Constant.PANEL_TYPE.*
 * @returns {import('recoil').RecoilState|null}
 */
export const getListOnDemandAtomByPanelType = (panelType) => {
    switch (panelType) {
        case Constant.PANEL_TYPE.CUSTOMER_TICKET_LIST:
            return listQAOnDemandAtom;
        case Constant.PANEL_TYPE.HELPDESK_TICKET_LIST:
            return listHelpdeskOnDemandAtom;
        case Constant.PANEL_TYPE.KNOWLEDGE_TICKET_LIST:
            return listKnowledgeOnDemandAtom;
        default:
            return listQAOnDemandAtom;
    }
};
